import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';

/** De drie rollen-panelen met Figma-accentkleur, diagram-asset en poortpaar. */
const HD_ROLE_GATE_PANELS = [
  {
    key: 'collective',
    accent: '#ffd028',
    image: 'assets/human-design/roles/role-gates-collective.png',
    extGate: 7,
    intGate: 13,
  },
  {
    key: 'individual',
    accent: '#eb9c26',
    image: 'assets/human-design/roles/role-gates-individual.png',
    extGate: 1,
    intGate: 10,
  },
  {
    key: 'tribal',
    accent: '#ff5959',
    image: 'assets/human-design/roles/role-gates-tribal.png',
    extGate: 27,
    intGate: 59,
  },
] as const;

/** Lijnvolgorde in de rol-lijsten (Figma: 6 bovenaan, 1 onderaan). */
const HD_ROLE_LIST_LINES: readonly number[] = [6, 5, 4, 3, 2, 1];

/** De 8 profileringspoorten in tabel-/lijstvolgorde (6 rolpoorten + 6 en 50). */
const HD_PROFILING_GATES: readonly number[] = [10, 7, 13, 1, 59, 27, 6, 50];
const HD_ROLE_TABLE_GATES: readonly number[] = [10, 7, 13, 1, 59, 27];
const HD_PROFILING_TABLE_GATES: readonly number[] = [6, 50];

/** Richting per tabelrij: kleurt het type-accent (int oranje, ext roest, bewustzijn rood). */
const HD_TABLE_ROW_DIRECTION: Readonly<Record<number, 'internalization' | 'externalization' | 'awareness'>> = {
  10: 'internalization', 7: 'externalization', 13: 'internalization',
  1: 'externalization', 59: 'internalization', 27: 'externalization',
  6: 'awareness', 50: 'awareness',
};

const HD_TABLE_DIRECTION_COLORS: Readonly<Record<'internalization' | 'externalization' | 'awareness', string>> = {
  internalization: '#eb9c26',
  externalization: '#cc6b4d',
  awareness: '#a7122d',
};

/** Figma-rijkleuren van de tabel. */
const HD_TABLE_ROW_COLORS: Readonly<Record<number, string>> = {
  10: '#fff1e9', 7: '#fff7db', 13: '#e8ffe5', 1: '#cffff6',
  59: '#c9f5ff', 27: '#e5e1ff', 6: '#ffe2eb', 50: '#ffe2e0',
};

interface RoleListItemVm {
  readonly line: number;
  readonly name: string;
  /** Engelse (of andere) glos tussen haakjes; leeg → verborgen. */
  readonly alt: string;
}

interface RolePanelVm {
  readonly key: string;
  readonly accent: string;
  readonly image: string;
  readonly title: string;
  readonly extHeading: string;
  readonly intHeading: string;
  readonly extCaption: string;
  readonly intCaption: string;
  readonly notes: readonly string[];
  readonly summary: string;
  readonly extRoles: readonly RoleListItemVm[];
  readonly intRoles: readonly RoleListItemVm[];
}

interface ProfilingItemVm {
  readonly gate: number;
  readonly name: string;
  readonly alt: string;
  readonly description: string;
}

interface RoleTableRowVm {
  readonly gate: number;
  readonly rowColor: string;
  readonly label: string;
  readonly lines: readonly string[];
  readonly typeMain: string;
  readonly typeAccent: string;
  readonly typeAccentColor: string;
  readonly personality: string;
  readonly design: string;
}

/**
 * Pagina die de Profiel & rol poorten cheat sheet toont, nagebouwd uit het
 * Figma-design "A3 - Profiles And Role Gates" (zonder A3-header/branding-
 * footer). Bovenaan de drie rollen-panelen (collectief/individueel/tribaal)
 * met de 12 rollen per domein, daarna de 8 rol- & profielpoorten (personaliteit
 * vs. design) en tot slot de grote lijnen-tabel per poort.
 *
 * Content komt uit de `human-design/role-gates`-scope via
 * {@link HdGateInfoService.cheatSheetTextIn}; de diagrammen zijn de Figma-
 * exports onder `assets/human-design/roles/`. Taalwissel via de gedeelde
 * content-taal-picker, nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-role-gates-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-role-gates-cheat-sheet-page.component.html',
  styleUrl: './hd-role-gates-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdRoleGatesCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));

  protected readonly panels = computed<readonly RolePanelVm[]>(() =>
    HD_ROLE_GATE_PANELS.map(panel => ({
      key: panel.key,
      accent: panel.accent,
      image: panel.image,
      title: this.text(`panels.${panel.key}.title`),
      extHeading: `${this.text('labels.externalization')} (${panel.extGate})`,
      intHeading: `${this.text('labels.internalization')} (${panel.intGate})`,
      extCaption: this.text(`panels.${panel.key}.extCaption`),
      intCaption: this.text(`panels.${panel.key}.intCaption`),
      notes: this.list(`panels.${panel.key}.notes`),
      summary: this.text(`panels.${panel.key}.summary`),
      extRoles: this.roleList(panel.key, 'ext'),
      intRoles: this.roleList(panel.key, 'int'),
    })),
  );

  protected readonly profilingTitle = computed(() => this.text('profiling.title'));
  protected readonly profilingPersonalityHeading = computed(() => this.text('profiling.personality.heading'));
  protected readonly profilingPersonalityNote = computed(() => this.text('profiling.personality.note'));
  protected readonly profilingDesignHeading = computed(() => this.text('profiling.design.heading'));
  protected readonly profilingDesignNote = computed(() => this.text('profiling.design.note'));

  protected readonly profilingPersonality = computed<readonly ProfilingItemVm[]>(() =>
    this.profilingItems('personality'),
  );

  protected readonly profilingDesign = computed<readonly ProfilingItemVm[]>(() =>
    this.profilingItems('design'),
  );

  protected readonly tableHeaders = computed(() => ({
    gate: this.text('table.headers.gate'),
    roleGates: this.text('table.headers.roleGates'),
    profilingGates: this.text('table.headers.profilingGates'),
    line: this.text('table.headers.line'),
    typeFocus: this.text('table.headers.typeFocus'),
    type: this.text('table.headers.type'),
    focus: this.text('table.headers.focus'),
    personalityDesign: this.text('table.headers.personalityDesign'),
  }));

  protected readonly roleTableRows = computed<readonly RoleTableRowVm[]>(() =>
    HD_ROLE_TABLE_GATES.map(gate => this.tableRow(gate)),
  );

  protected readonly profilingTableRows = computed<readonly RoleTableRowVm[]>(() =>
    HD_PROFILING_TABLE_GATES.map(gate => this.tableRow(gate)),
  );

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  private roleList(panelKey: string, direction: 'ext' | 'int'): readonly RoleListItemVm[] {
    return HD_ROLE_LIST_LINES.map(line => ({
      line,
      name: this.text(`panels.${panelKey}.${direction}Roles.${line}.name`),
      alt: this.optionalText(`panels.${panelKey}.${direction}Roles.${line}.alt`),
    }));
  }

  private profilingItems(side: 'personality' | 'design'): readonly ProfilingItemVm[] {
    return HD_PROFILING_GATES.map(gate => ({
      gate,
      name: this.text(`profiling.${side}.items.${gate}.name`),
      alt: this.optionalText(`profiling.${side}.items.${gate}.alt`),
      description: this.text(`profiling.${side}.items.${gate}.description`),
    }));
  }

  private tableRow(gate: number): RoleTableRowVm {
    const direction = HD_TABLE_ROW_DIRECTION[gate];
    return {
      gate,
      rowColor: HD_TABLE_ROW_COLORS[gate],
      label: this.text(`table.rows.${gate}.label`),
      lines: [1, 2, 3, 4, 5, 6].map(line => this.text(`table.rows.${gate}.line${line}`)),
      typeMain: this.text(`table.rows.${gate}.type`),
      typeAccent: this.text(`table.directions.${direction}`),
      typeAccentColor: HD_TABLE_DIRECTION_COLORS[direction],
      personality: this.text(`table.rows.${gate}.personality`),
      design: this.text(`table.rows.${gate}.design`),
    };
  }

  /**
   * Scope-tekst in de content-taal zonder NL-fallback: voor optionele glossen
   * (de Engelse naam onder de Nederlandse rol) die in het EN zelf leeg blijven.
   */
  private optionalText(key: string): string {
    return this.gateInfo.cheatSheetTextIn('roleGates', key, this.lang());
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('roleGates', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('roleGates', key, 'nl');
  }

  /** Scope-string-array in de content-taal, met dezelfde NL-fallback als {@link text}. */
  private list(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetListIn('roleGates', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('roleGates', key, 'nl');
  }
}
